import React, { useMemo } from 'react';
import { Tooltip, type ITooltip } from 'react-tooltip';
import { CloseButton, CloseButtonColor } from '../LiliCloseButton/CloseButton';
import { twMerge } from 'tailwind-merge';
import { FontWeight, Text } from '../../tokens';

export enum TooltipPosition {
  TOP = 'top',
  TOP_START = 'top-start',
  TOP_END = 'top-end',
  BOTTOM = 'bottom',
  BOTTOM_START = 'bottom-start',
  BOTTOM_END = 'bottom-end',
  LEFT = 'left',
  LEFT_START = 'left-start',
  LEFT_END = 'left-end',
  RIGHT = 'right',
  RIGHT_START = 'right-start',
  RIGHT_END = 'right-end',
}

export enum LiliToolTipColor {
  White,
  Blue,
  Black,
}

export interface LiliToolTipProps extends Omit<ITooltip, 'position'> {
  title?: string;
  description: string;
  showCloseButton?: boolean;
  targetId: string;
  color?: LiliToolTipColor;
  position?: TooltipPosition;
  onClose?: () => void;
  customContent?: React.ReactNode;
}

export const LiliTooltip = ({
  title,
  description,
  showCloseButton,
  targetId,
  color = LiliToolTipColor.Black,
  position = TooltipPosition.BOTTOM,
  isOpen,
  onClose,
  customContent,
  className,
  ...rest
}: LiliToolTipProps) => {
  const backgroundColor = useMemo(() => {
    let colorClass: string;
    switch (color) {
      case LiliToolTipColor.White:
        colorClass = '#ffffff';
        break;
      case LiliToolTipColor.Black:
        colorClass = '#20242E';
        break;
      case LiliToolTipColor.Blue:
        colorClass = '#0E5CF4';
        break;
      default:
        colorClass = '#ffffff';
    }
    return colorClass;
  }, [color]);
  const textColor = () => {
    switch (color) {
      case LiliToolTipColor.White:
        return 'text-text-main-secondary';
      case LiliToolTipColor.Black:
        return 'text-text-main-primary-inverse';
      case LiliToolTipColor.Blue:
        return 'text-text-main-primary-inverse';
      default:
        return 'text-text-main-primary-inverse';
    }
  };

  const closeButtonColor = () => {
    switch (color) {
      case LiliToolTipColor.White:
        return CloseButtonColor.Gray;
      case LiliToolTipColor.Black:
        return CloseButtonColor.White;
      case LiliToolTipColor.Blue:
        return CloseButtonColor.White;
      default:
        return CloseButtonColor.Gray;
    }
  };
  const tooltipStyle = twMerge('z-[999] !p-0 !opacity-100 max-w-[290px] !rounded-[8px]', className);
  return (
    <Tooltip
      {...rest}
      id={targetId}
      className={tooltipStyle}
      border={color === LiliToolTipColor.White ? '1px solid #DFE2EB' : ''}
      place={position}
      isOpen={isOpen === undefined ? undefined : isOpen}
      style={{ backgroundColor, ...rest.style }}
      hidden={isOpen !== undefined && !isOpen}
      clickable>
      <div className='flex flex-row '>
        {customContent || (
          <div className='flex flex-col px-[16px] py-[12px] h-fit'>
            <Text.H9 fontWeight={FontWeight.SEMIBOLD} className={textColor()}>
              {title}
            </Text.H9>
            <Text.H9 className={textColor()}>{description}</Text.H9>
          </div>
        )}
        {showCloseButton && (
          <CloseButton
            id={`${targetId}-close-button`}
            color={closeButtonColor()}
            onClick={onClose}
            className='mt-[8px] mr-[12px]'
          />
        )}
      </div>
    </Tooltip>
  );
};
